import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { usePreviewMode } from "../../hooks/usePreviewMode";
import Pic from "@/components/common/Pic";

const ExperienceAtFarmSection = () => {
  const { t, i18n } = useTranslation();
  const isPreviewMode = usePreviewMode();
  // Extract base language code (e.g., 'en' from 'en-US' or 'en-US@posix')
  const lang = i18n.language?.split('-')[0]?.split('@')[0] || 'en';
  
  // Base experiences always shown
  const baseExperiences = [
    {
      name: t('ui.experienceAtFarm.fruitTrees.name'),
      description: t('ui.experienceAtFarm.fruitTrees.description'),
      image: "/images/experiences/fruit_tree.webp",
      link: `/${lang}/experiences/fruit-trees`
    },
    {
      name: t('ui.experienceAtFarm.greenhouse.name'),
      description: t('ui.experienceAtFarm.greenhouse.description'),
      image: "/images/experiences/e01.webp",
      link: `/${lang}/experiences/greenhouse`
    },
    {
      name: t('ui.experienceAtFarm.animals.name'),
      description: t('ui.experienceAtFarm.animals.description'),
      image: "/images/experiences/e02.webp",
      link: null
    },
    {
      name: t('ui.experienceAtFarm.birdwatching.name'),
      description: t('ui.experienceAtFarm.birdwatching.description'),
      image: "/images/experiences/bird.webp",
      link: `/${lang}/experiences/birds`
    }
  ];

  // Preview-only experiences (hidden from public, visible with ?preview=true)
  const previewExperiences = [
    {
      name: t('ui.experienceAtFarm.regenForest.name', 'Regenerative Forest'),
      description: t('ui.experienceAtFarm.regenForest.description', 'Plant a tree and help restore the forest'),
      image: "/images/experiences/e03.webp",
      link: `/${lang}/arrabida-biosfera`,
      isPreview: true
    }
  ];

  // Combine experiences based on preview mode
  const experiences = isPreviewMode 
    ? [...baseExperiences.slice(0, 2), ...previewExperiences, ...baseExperiences.slice(2)]
    : baseExperiences;

  return (
    <section className="py-16 md:py-24 bg-white" data-testid="experience-at-farm-section">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="pretitle">{t('ui.experienceAtFarm.pretitle')}</span>
          <h2 className="font-sans text-2xl md:text-3xl text-[#33685F] font-semibold mb-4">
            {t('ui.experienceAtFarm.title')}
          </h2>
          <p className="font-sans text-sm md:text-base text-[#48756B] max-w-2xl mx-auto mb-3">
            {t('ui.experienceAtFarm.description1', 'Immerse yourself in the rhythms of life within nature.')}
          </p>
          <p className="font-sans text-sm md:text-base text-[#48756B] max-w-2xl mx-auto">
            {t('ui.experienceAtFarm.description2', 'Pick seasonal fruits, explore our regenerative forest, enjoy birdwatching by the lake, or even plant a tree with your children!')}
          </p>
        </div>
        
        {/* Experiences Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {experiences.map((experience, index) => {
            const content = (
              <>
                <div className="overflow-hidden mb-3 relative">
                  {experience.isPreview && (
                    <span className="absolute top-2 left-2 z-10 px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded font-medium">
                      PREVIEW
                    </span>
                  )}
                  <Pic
                    src={experience.image}
                    alt={experience.name}
                    className="w-full h-44 md:h-48 object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-300"
                    loading="lazy"
                  />
                </div>
                <p className="font-sans text-[#33685F] font-bold text-sm md:text-base">
                  {experience.name}
                </p>
                <p className="font-sans text-[#3F7A6E] text-xs md:text-sm">
                  {experience.description}
                </p>
              </>
            );

            return experience.link ? (
              <Link 
                key={index} 
                to={experience.link}
                className="group block"
              >
                {content}
              </Link>
            ) : (
              <div key={index} className="group block">
                {content}
              </div>
            );
          })}
        </div>

        {/* Explore with Children Subsection - Only visible in preview mode */}
        {isPreviewMode && (
          <div className="mt-16 pt-12 border-t border-[#e8e4df]" data-testid="explore-with-children-section">
            <div className="text-center">
              <span className="inline-block px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded mb-4">PREVIEW MODE</span>
              <h3 className="font-sans text-xl md:text-2xl text-[#33685F] font-semibold mb-3">
                {t('ui.exploreWithChildren.title', 'Explore the Farm with Your Children')}
              </h3>
              <p className="font-sans text-sm md:text-base text-[#48756B] max-w-2xl mx-auto mb-6">
                {t('ui.exploreWithChildren.cta', 'Discover all we prepared for you.')}
              </p>
              <Link 
                to={`/${lang}/experiences/children`}
                className="inline-block px-8 py-3 border border-[#33685F] text-[#33685F] font-sans uppercase tracking-[0.15em] text-xs transition-all duration-300 hover:bg-[#33685F] hover:text-white"
              >
                {t('ui.exploreWithChildren.button', 'Explore Activities')}
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ExperienceAtFarmSection;
